import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import AdminSbtLandingPages from "@/pages/AdminSbtLandingPages";
import AdminSbtSponsors from "@/pages/AdminSbtSponsors";
import AdminSbtTrendingTopics from "@/pages/AdminSbtTrendingTopics";
import AdminSbtStats from "@/pages/AdminSbtStats";
import AdminSbtAudienceCounts from "@/pages/AdminSbtAudienceCounts";
import AdminSbtHeroSlider from "@/pages/AdminSbtHeroSlider";
import AdminSbtCareers from "@/pages/AdminSbtCareers";
import AdminSbtContact from "@/pages/AdminSbtContact";
import {
  AdminSbtApproachSection,
  AdminSbtCtaMegaSection,
  AdminSbtFooterSection,
  AdminSbtPartnerEcoSection,
  AdminSbtAiSearchSection,
  AdminSbtLegalPagesSection,
} from "@/pages/AdminSbtSections";
import PublishChangesButton from "@/components/PublishChangesButton";
import AdminSbtPageEditor from "@/pages/AdminSbtPageEditor";
import AdminSbtSEO from "@/pages/AdminSbtSEO";
import AdminSbtSolutions from "@/pages/AdminSbtSolutions";
import AdminSbtCompetitors from "@/pages/AdminSbtCompetitors";
import AdminSbtAIAutopilot from "@/pages/AdminSbtAIAutopilot";
import AdminSbtWhatsApp from "@/pages/AdminSbtWhatsApp";
import AdminSbtWhatsAppLeads from "@/pages/AdminSbtWhatsAppLeads";
import AdminSbtServiceKB from "@/pages/AdminSbtServiceKB";
import AdminSbtAIVisibility from "@/pages/AdminSbtAIVisibility";

/**
 * AdminSbtSite — top-level tabbed admin covering every page of the
 * marketing site in this exact sequence:
 *   Home · About Company · Services · Owned Media · Media Kit ·
 *   Market Plus · Resources · Careers · Contact Us
 *
 * Tabs contain the CRUD editors we've built so far. Any tab still
 * marked with a "Coming soon" scaffold will be filled in follow-up
 * iterations — the tab structure is already stable so nothing shifts
 * around when new editors land.
 */
const PageStub = ({ title, description, sections }) => (
  <div className="rounded border border-dashed border-slate-300 bg-slate-50 p-6" data-testid={`stub-${title.replace(/\W+/g, "-").toLowerCase()}`}>
    <h3 className="text-base font-semibold text-slate-900">{title} — editor coming next</h3>
    <p className="text-sm text-slate-600 mt-1">{description}</p>
    {sections && sections.length > 0 && (
      <div className="mt-4">
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Editable sections planned</div>
        <ul className="grid grid-cols-2 gap-1 text-sm text-slate-700">
          {sections.map((s) => (
            <li key={s} className="flex items-center gap-2">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-cyan-500"/>
              {s}
            </li>
          ))}
        </ul>
      </div>
    )}
    <p className="text-xs text-slate-400 mt-4 italic">
      The Sponsor &amp; Exhibitor editor (under Home) is the template — every section here will be add / edit / delete / reorder-able using the same pattern.
    </p>
  </div>
);

export default function AdminSbtSite() {
  return (
    <div data-testid="sbt-site-admin">
      <div className="sticky top-0 z-30 bg-white border-b border-slate-200 -mx-6 px-6 py-3 mb-4 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold tracking-tight">SBT Marketing Site — Content Manager</h2>
          <p className="text-sm text-slate-500 mt-1">
            Edit any section, then click <span className="font-semibold text-cyan-700">Publish Changes</span> to push updates live to the marketing site.
          </p>
        </div>
        <PublishChangesButton />
      </div>

      <Tabs defaultValue="home" className="w-full">
        <TabsList className="flex flex-wrap justify-start h-auto gap-1">
          <TabsTrigger value="home"          data-testid="site-tab-home">Home</TabsTrigger>
          <TabsTrigger value="about"         data-testid="site-tab-about">About Company</TabsTrigger>
          <TabsTrigger value="services"      data-testid="site-tab-services">Services</TabsTrigger>
          <TabsTrigger value="owned_media"   data-testid="site-tab-owned-media">Owned Media</TabsTrigger>
          <TabsTrigger value="media_kit"     data-testid="site-tab-media-kit">Media Kit</TabsTrigger>
          <TabsTrigger value="market_plus"   data-testid="site-tab-market-plus">Market Plus</TabsTrigger>
          <TabsTrigger value="resources"     data-testid="site-tab-resources">Resources</TabsTrigger>
          <TabsTrigger value="careers"       data-testid="site-tab-careers">Careers</TabsTrigger>
          <TabsTrigger value="contact"       data-testid="site-tab-contact">Contact Us</TabsTrigger>
          <TabsTrigger value="legal"         data-testid="site-tab-legal">Legal Pages</TabsTrigger>
          <TabsTrigger value="seo"           data-testid="site-tab-seo">SEO / AEO</TabsTrigger>
          <TabsTrigger value="solutions"     data-testid="site-tab-solutions">Solutions Matrix</TabsTrigger>
          <TabsTrigger value="competitors"   data-testid="site-tab-competitors">Competitor Watch</TabsTrigger>
          <TabsTrigger value="autopilot"     data-testid="site-tab-autopilot">AI Autopilot</TabsTrigger>
          <TabsTrigger value="whatsapp"      data-testid="site-tab-whatsapp">WhatsApp Inbox</TabsTrigger>
          <TabsTrigger value="widget_leads"  data-testid="site-tab-widget-leads">Widget Leads</TabsTrigger>
          <TabsTrigger value="ai_visibility" data-testid="site-tab-ai-visibility">AI Visibility</TabsTrigger>
        </TabsList>

        {/* ── HOME ─────────────────────────────────────────── */}
        <TabsContent value="home" className="mt-6 space-y-10">
          <SectionHeader title="Homepage" subtitle="Hero, stats, trending topics, partner ecosystem, sponsors & CTAs."/>
          <AdminSbtHeroSlider />
          <div className="border-t pt-8"><AdminSbtStats /></div>
          <div className="border-t pt-8"><AdminSbtTrendingTopics /></div>
          <div className="border-t pt-8"><AdminSbtSponsors /></div>
          <div className="border-t pt-8"><AdminSbtPartnerEcoSection /></div>
          <div className="border-t pt-8"><AdminSbtAiSearchSection /></div>
          <div className="border-t pt-8"><AdminSbtApproachSection /></div>
          <div className="border-t pt-8"><AdminSbtCtaMegaSection /></div>
          <div className="border-t pt-8"><AdminSbtFooterSection /></div>
        </TabsContent>

        {/* ── ABOUT COMPANY ────────────────────────────────── */}
        <TabsContent value="about" className="mt-6">
          <SectionHeader title="About Company" subtitle="Mission, values, team, history."/>
          <AdminSbtPageEditor
            sectionKey="page_about"
            title="About page content"
            description="Hero + Story + 4 Pillars + Numbers + Team roster. Rich lists edited as JSON."
            extraFields={["story"]}
            arrayFields={["pillars", "numbers", "team"]}
          />
        </TabsContent>

        {/* ── SERVICES ─────────────────────────────────────── */}
        <TabsContent value="services" className="mt-6 space-y-10">
          <SectionHeader title="Services" subtitle="Six-service catalog with bullets."/>
          <AdminSbtPageEditor
            sectionKey="page_services"
            title="Services page content"
            description="Hero + detailed service cards. Each item = { id, tag, title, summary, bullets: [] }."
            arrayFields={["detailed"]}
          />
          <div className="border-t pt-8">
            <AdminSbtPageEditor
              sectionKey="service_catalog"
              title="AI Capability Matrix — 3 columns"
              description="Powers the 'Capability Matrix' grid on /services + MainNav mega-dropdown + Media Kit page. Each group has an editable bullet list (AI Powered Solutions, AI Powered Product, Data)."
              arrayFields={["groups"]}
            />
          </div>
          <div className="border-t pt-8">
            <SectionHeader
              title="Service Knowledge Base — 38 detail pages"
              subtitle="One editable entry per service — powers /services/[slug]/ detail pages, WhatsApp AI Concierge context, and future on-site chat. Edit description (60–100 words), hero photo, key benefits, FAQ, pricing signal and case snapshot."
            />
            <AdminSbtServiceKB />
          </div>
        </TabsContent>

        {/* ── OWNED MEDIA ──────────────────────────────────── */}
        <TabsContent value="owned_media" className="mt-6">
          <SectionHeader title="Owned Media" subtitle="10 owned brands + metrics + ad formats."/>
          <AdminSbtPageEditor
            sectionKey="page_media"
            title="Owned Media page content"
            description="Hero + 4 metric pills + 10 property cards + 4 ad-format blurbs."
            arrayFields={["metrics", "properties", "formats"]}
          />
        </TabsContent>

        {/* ── MEDIA KIT ────────────────────────────────────── */}
        <TabsContent value="media_kit" className="mt-6">
          <SectionHeader title="Media Kit" subtitle="Hero, atom orbit satellites, six 'Forwardism' pillars, downloadable PDF."/>
          <AdminSbtPageEditor
            sectionKey="page_media_kit"
            title="Media Kit page content"
            description="Hero (with PDF download URL) + 5 orbit satellites + 6 capability pillars."
            arrayFields={["orbit", "pillars"]}
          />
          <div className="border-t pt-6">
            <AdminSbtPageEditor
              sectionKey="mk_forwardism"
              title="Forwardism — capability pillars"
              description="Eyebrow, title and the pillar cards (name, color, description)."
              arrayFields={["pillars"]}
            />
          </div>
          <div className="border-t pt-6">
            <AdminSbtPageEditor
              sectionKey="mk_about"
              title="About Company / Our Vision"
              description="Vision copy, the five Company Vision cards, and the Who We Are paragraph."
              arrayFields={["vision_cards"]}
            />
          </div>
          <div className="border-t pt-6">
            <AdminSbtPageEditor
              sectionKey="mk_highlights"
              title="Highlights"
              description="Eyebrow, title, subtitle and the capability bullet list."
              arrayFields={["items"]}
            />
          </div>
          <div className="border-t pt-6">
            <AdminSbtPageEditor
              sectionKey="mk_ai_banner"
              title="AI banner / tagline strip"
              description="Eyebrow, title and lead line for the Forwardism tagline banner."
            />
          </div>
        </TabsContent>

        {/* ── MARKET PLUS ──────────────────────────────────── */}
        <TabsContent value="market_plus" className="mt-4 space-y-6">
          {/* NOTE: intentionally no SectionHeader here — AdminSbtLandingPages
              already renders its own "Content Library — Landing Pages"
              heading, so a duplicate SectionHeader added ~350px of
              redundant whitespace above the table. */}
          <AdminSbtLandingPages />
          <div className="border-t pt-6"><AdminSbtAudienceCounts /></div>
        </TabsContent>

        {/* ── RESOURCES ────────────────────────────────────── */}
        <TabsContent value="resources" className="mt-6 space-y-10">
          <SectionHeader title="Resources Hub" subtitle="Hero, category cards, CTA banner + underlying whitepaper library."/>
          <AdminSbtPageEditor
            sectionKey="page_resources"
            title="Resources Hub page content"
            description="Hero + featured category cards + submit-a-topic CTA banner."
            arrayFields={["featured_categories"]}
            extraFields={["cta_banner"]}
          />
          <div className="border-t pt-8">
            <div className="text-xs text-slate-500 mb-3">
              Whitepapers / case studies themselves live in <span className="font-semibold">Market Plus → Landing Pages</span> — no duplication needed.
            </div>
          </div>
        </TabsContent>

        {/* ── CAREERS ──────────────────────────────────────── */}
        <TabsContent value="careers" className="mt-6">
          <SectionHeader title="Careers" subtitle="Open roles CRUD + inbound applications (with resume) from the marketing site."/>
          <AdminSbtCareers />
        </TabsContent>

        {/* ── CONTACT ──────────────────────────────────────── */}
        <TabsContent value="contact" className="mt-6">
          <SectionHeader title="Contact Us" subtitle="Form recipients, office addresses, socials & map — also powers the Footer contact strip."/>
          <AdminSbtContact />
        </TabsContent>

        {/* ── SEO / AEO ────────────────────────────────────── */}
        <TabsContent value="legal" className="mt-6">
          <AdminSbtLegalPagesSection />
        </TabsContent>

        <TabsContent value="seo" className="mt-6">
          <SectionHeader
            title="SEO / AEO"
            subtitle="Meta title, description, keywords, OG image and FAQs per page + global organization schema. Directly controls how ChatGPT, Perplexity, Google AI Overviews and Bing Copilot cite SBT Research."
          />
          <AdminSbtSEO />
        </TabsContent>

        {/* ── SOLUTIONS MATRIX ─────────────────────────────── */}
        <TabsContent value="solutions" className="mt-6">
          <SectionHeader
            title="Programmatic Solutions"
            subtitle="Industries × Use-cases matrix. Every combination auto-generates one answer-style landing page under /solutions/{industry}/{use-case}/ — full AI-answer-engine optimised. Per-combo override lets you hand-craft key pages."
          />
          <AdminSbtSolutions />
        </TabsContent>

        {/* ── COMPETITOR WATCH ─────────────────────────────── */}
        <TabsContent value="competitors" className="mt-6">
          <SectionHeader
            title="Competitor Watch"
            subtitle="24×7 daily crawler of 8 rival demand-gen firms. Claude Sonnet diffs each homepage + blog and drafts a suggested SBT response for every material change."
          />
          <AdminSbtCompetitors />
        </TabsContent>

        {/* ── AI AUTOPILOT (Content Farm + Case Studies) ───── */}
        <TabsContent value="autopilot" className="mt-6">
          <SectionHeader
            title="AI Content Autopilot"
            subtitle="24×7 auto-generated whitepapers, eBooks, buyer's guides and illustrative case studies. Every draft holds for admin approval before publishing to the live site."
          />
          <AdminSbtAIAutopilot />
        </TabsContent>

        {/* ── WHATSAPP INBOX ───────────────────────────────── */}
        <TabsContent value="whatsapp" className="mt-6">
          <SectionHeader
            title="WhatsApp AI Concierge"
            subtitle="24×7 AI concierge on the marketing site's floating WhatsApp bubble. Auto-answers FAQs, escalates meeting / pricing / human requests to admin WhatsApp with a full reply-relay bridge back to the visitor."
          />
          <AdminSbtWhatsApp />
        </TabsContent>

        {/* ── WIDGET LEADS ─────────────────────────────────── */}
        <TabsContent value="widget_leads" className="mt-6">
          <SectionHeader
            title="WhatsApp Widget Leads"
            subtitle="Warm leads captured from the in-browser chat widget. Every visitor who submits the progressive-profile card lands here with their full transcript, ready for one-click follow-up via WhatsApp or email."
          />
          <AdminSbtWhatsAppLeads />
        </TabsContent>

        {/* ── AI VISIBILITY (SEMrush) ───────────────────────── */}
        <TabsContent value="ai_visibility" className="mt-6">
          <SectionHeader
            title="AI Visibility Monitor"
            subtitle="SEMrush-backed dashboard tracking sbtresearch.com organic rank, top keywords and competitor overlap. Nightly at 04:00 IST."
          />
          <AdminSbtAIVisibility />
        </TabsContent>
      </Tabs>
    </div>
  );
}

const SectionHeader = ({ title, subtitle }) => (
  <div className="pb-4 border-b border-slate-200 mb-2">
    <h3 className="text-base font-semibold text-slate-900">{title}</h3>
    <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>
  </div>
);
